import React from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  CircularProgress
} from '@material-ui/core';
import { useStyles } from './styles';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useTranslation } from 'react-i18next';
import { CreateUpdateVeterinaryCommand, VeterinaryViewModel } from '../../models/vets';
import VetService from '../../hooks/VetService';

interface CreateUpdateVeterinaryModalProps {
  opened: boolean;
  onModalAction: (result: boolean | null) => void;
  mode: 'create' | 'update';
  vet: VeterinaryViewModel | null;
}

const CreateUpdateVeterinaryModal: React.FC<any> = ({
  opened,
  onModalAction,
  mode,
  vet
}: CreateUpdateVeterinaryModalProps) => {
  const classes = useStyles();
  const vetService = new VetService();
  const { t } = useTranslation();

  const [name, setName] = React.useState(vet?.name || '');
  const [number, setNumber] = React.useState(vet?.name || '');
  const [country, setCountry] = React.useState(vet?.settings?.country || '');
  const [address, setAddress] = React.useState(vet?.settings?.address || '');
  const [phone, setPhone] = React.useState(vet?.settings?.phone || '');
  const [email, setEmail] = React.useState(vet?.settings?.email || '');

  const [isBusy, setIsBusy] = React.useState(false);

  React.useEffect(() => {
    if (vet) {
      setName(vet?.name || '');
      setNumber(vet?.number || '');
      setCountry(vet?.settings?.country || '');
      setAddress(vet?.settings?.address || '');
      setPhone(vet?.settings?.phone || '');
      setEmail(vet?.settings?.email || '');
    }
  }, [vet, opened]);

  const closeDialog = () => {
    clearFields();
    onModalAction(null);
  };

  const handleCreateVet = () => {
    if (!validateForm()) {
      return;
    }

    setIsBusy(true);

    vetService
      .createVet(buildVetCommand())
      .then(() => {
        notifications.success(t('f-add-ok'));
        clearFields();
        onModalAction(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleUpdateVet = () => {
    if (!validateForm()) {
      return;
    }

    setIsBusy(true);

    vetService
      .updateVet(buildVetCommand())
      .then(() => {
        notifications.success(t('f-upd-ok'));
        clearFields();
        onModalAction(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const buildVetCommand = () => {
    return {
      name,
      id: vet?.id || null,
      number: mode === 'create' ? number : null,
      settings: {
        country,
        address,
        phone,
        email
      }
    } as CreateUpdateVeterinaryCommand;
  };

  const clearFields = () => {
    setName('');
    setNumber('');
    setCountry('');
    setAddress('');
    setPhone('');
    setEmail('');
  };

  const handleSaveClick = () => {
    if (mode === 'create') {
      handleCreateVet();
    } else if (mode === 'update') {
      handleUpdateVet();
    }
  };

  const validateForm = (): boolean => {
    if (!name) {
      notifications.warning(t('f-val-name'));
      return false;
    }

    if (mode === 'create' && !number) {
      notifications.warning(t('f-val-number'));
      return false;
    }

    // if (!email) {
    //   notifications.warning(t('f-val-email'));
    //   return false;
    // } else {
    //   if (!validateEmail(email)) {
    //     notifications.warning(t('f-val-email'));
    //     return false;
    //   }
    // }

    return true;
  };

  return (
    <>
      <Dialog id="vetDialog" open={opened} onClose={closeDialog} className={classes.dialog} maxWidth="sm">
        <DialogTitle id="vetDialogTitle" className={classes.dialogHeaderFooter}>
          {mode === 'create' ? t('f-vets-add') : t('f-vets-upd')}
        </DialogTitle>

        <DialogContent>
          <Grid item container direction="column">
            <Grid item>
              <TextField
                label={t('f-com-number')}
                size="small"
                fullWidth
                disabled={mode === 'update'}
                variant="outlined"
                className={classes.formControl}
                value={number}
                onChange={(e) => {
                  setNumber(e.target.value);
                }}
              />
            </Grid>

            <Grid item>
              <TextField
                label={t('f-com-name')}
                size="small"
                fullWidth
                variant="outlined"
                className={classes.formControl}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </Grid>

            <Grid item>
              <TextField
                label={t('f-country')}
                size="small"
                fullWidth
                variant="outlined"
                className={classes.formControl}
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                }}
              />
            </Grid>

            <Grid item>
              <TextField
                label={t('f-com-address')}
                size="small"
                fullWidth
                variant="outlined"
                className={classes.formControl}
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                }}
              />
            </Grid>

            <Grid item>
              <TextField
                label={t('f-com-phone')}
                size="small"
                fullWidth
                variant="outlined"
                className={classes.formControl}
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                }}
              />
            </Grid>

            <Grid item>
              <TextField
                label={t('f-vets-mail-back')}
                size="small"
                fullWidth
                variant="outlined"
                className={classes.formControl}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions className={classes.dialogHeaderFooter}>
          {isBusy && <CircularProgress />}

          <Button
            onClick={closeDialog}
            variant="outlined"
            disabled={isBusy}
            startIcon={<Icon>{'close'}</Icon>}
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>

          <Button
            onClick={handleSaveClick}
            id="createFarmBtn"
            disabled={isBusy}
            startIcon={<Icon>{'save'}</Icon>}
            variant="contained"
            color="primary"
          >
            {t('f-com-save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateUpdateVeterinaryModal;
